import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';

import {
  approveOrgAdminRequest,
  createOrganization,
  inviteUrlFor,
  listOrgAdminRequests,
  listOrgBatches,
  listOrganizations,
  rejectOrgAdminRequest,
} from '../../api/organizationApi';
import { useAuth } from '../../contexts/AuthContext';
import '../school/SchoolPlan.css';
import './OrganizationAdminPage.css';

const STATUS_TABS = [
  { id: 'pending', label: '확인 대기' },
  { id: 'approved', label: '승인' },
  { id: 'rejected', label: '반려' },
];

const SOURCE_LABEL = { bulk_order: '단체구매 주문', admin_case: '계약 건' };
const LEVEL_LABEL = { elementary: '초등', middle: '중학교', high: '고등학교', other: '기타' };

const formatDate = (iso) => (iso ? iso.slice(0, 10) : '');

const candidateKey = (candidate) => `${candidate.source_type}:${candidate.source_id}`;

const MAX_SEARCH_RESULTS = 5;

export const InviteLinkCell = ({ token }) => {
  const [copied, setCopied] = useState('');
  if (!token) return <span className="org-admin-hint">-</span>;
  const url = inviteUrlFor(token);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied('복사했어요');
    } catch (_error) {
      setCopied('복사 실패 — 링크를 직접 선택해 주세요');
    }
  };
  return (
    <span className="org-admin-invite">
      <button type="button" className="school-btn-ghost" onClick={copy} title={url}>초대 링크 복사</button>
      {copied && <span className="org-admin-hint" role="status">{copied}</span>}
    </span>
  );
};

export const searchBatches = (batches, query, excludeKeys = new Set()) => {
  const needle = query.trim().toLowerCase();
  if (!needle) return [];
  return batches
    .filter((b) => !excludeKeys.has(candidateKey(b)))
    .filter((b) => (b.name || '').toLowerCase().includes(needle) || (b.source_id || '').toLowerCase().includes(needle))
    .slice(0, MAX_SEARCH_RESULTS);
};

const CandidateOption = ({ candidate, groupName, selected, onSelect }) => (
  <label className={selected ? 'is-selected' : ''}>
    <input type="radio" name={groupName} checked={selected} onChange={onSelect} />
    <span className="org-admin-cand-main">
      <strong>{candidate.name || '이름 없음'}</strong>
      <span>
        {SOURCE_LABEL[candidate.source_type]} · {candidate.quantity}명 · {formatDate(candidate.created_at)}
        {candidate.org_exists ? ' · 학교 이미 있음(관리자만 추가)' : ''}
      </span>
    </span>
  </label>
);

export const RequestCard = ({ request, batches = [], onApprove, onReject, busy }) => {
  const [selected, setSelected] = useState(
    request.candidates?.length === 1 ? candidateKey(request.candidates[0]) : '',
  );
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [query, setQuery] = useState('');
  const candidates = useMemo(() => request.candidates || [], [request.candidates]);
  const found = useMemo(
    () => searchBatches(batches, query, new Set(candidates.map(candidateKey))),
    [batches, query, candidates],
  );
  const chosen = [...candidates, ...found].find((c) => candidateKey(c) === selected);
  const pending = request.status === 'pending';

  return (
    <article className="org-admin-request" data-testid="org-admin-request">
      <header className="org-admin-request-head">
        <div>
          <h3>{request.school_name || '학교 이름 없음'}</h3>
          <p>
            {[LEVEL_LABEL[request.school_level], request.teacher_count ? `선생님 ${request.teacher_count}명` : '', formatDate(request.created_at)]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
        <div className="org-admin-badges">
          {request.account_email_matches_purchase && (
            <span className="org-admin-badge is-match">구매 이메일 일치</span>
          )}
          {!request.account_email_matches_purchase && request.given_purchase_email_found && (
            <span className="org-admin-badge is-partial">적어 준 구매 이메일로 주문 확인</span>
          )}
          {!request.candidates?.length && <span className="org-admin-badge is-none">구매 내역 못 찾음</span>}
        </div>
      </header>

      <dl className="org-admin-facts">
        <div><dt>신청 계정</dt><dd>{request.email || '-'}</dd></div>
        <div><dt>구매 이메일</dt><dd>{request.purchase_email || '-'}</dd></div>
        <div><dt>주문·견적 번호</dt><dd>{request.purchase_ref || '-'}</dd></div>
        <div><dt>연락처</dt><dd>{request.phone || '-'}</dd></div>
        {request.message && <div className="is-wide"><dt>남긴 말</dt><dd>{request.message}</dd></div>}
      </dl>

      {pending && (
        <>
          <fieldset className="org-admin-candidates">
            <legend>좌석으로 쓸 구매 내역</legend>
            {candidates.map((candidate) => (
              <CandidateOption
                key={candidateKey(candidate)}
                candidate={candidate}
                groupName={`cand-${request.id}`}
                selected={selected === candidateKey(candidate)}
                onSelect={() => setSelected(candidateKey(candidate))}
              />
            ))}
            {!candidates.length && (
              <p className="org-admin-hint">신청 계정·구매 이메일로 결제 완료된 단체구매나 계약 건을 찾지 못했어요. 아래에서 학교 이름으로 찾거나, 연락해서 확인해 주세요.</p>
            )}
            <input
              className="org-admin-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="학교 이름·주문번호로 10명 이상 구매 내역 찾기"
              aria-label="구매 내역 찾기"
            />
            {found.map((candidate) => (
              <CandidateOption
                key={candidateKey(candidate)}
                candidate={candidate}
                groupName={`cand-${request.id}`}
                selected={selected === candidateKey(candidate)}
                onSelect={() => setSelected(candidateKey(candidate))}
              />
            ))}
            {query.trim() && !found.length && <p className="org-admin-hint">맞는 구매 내역이 없어요.</p>}
          </fieldset>

          <div className="org-admin-actions">
            <p className="org-admin-hint" data-testid="org-admin-plan-hint">학교(엔터프라이즈)는 12개월권 · 10자리 이상 주문만 열 수 있어요. 생기부 검토까지 함께 열려요.</p>
            <div className="org-admin-buttons">
              <button type="button" className="school-btn-ghost" onClick={() => setRejecting((v) => !v)} disabled={busy}>
                반려하기
              </button>
              <button
                type="button"
                className="school-btn-primary"
                disabled={!chosen || busy}
                onClick={() => onApprove(request, chosen)}
              >
                관리자로 승인하기
              </button>
            </div>
          </div>

          {rejecting && (
            <div className="org-admin-reject">
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="신청자에게 보일 반려 이유"
                maxLength={300}
                aria-label="반려 이유"
              />
              <button type="button" className="school-btn-secondary" disabled={busy} onClick={() => onReject(request, reason)}>
                반려 보내기
              </button>
            </div>
          )}
        </>
      )}

      {request.status === 'approved' && <p className="org-admin-hint">학교 ID: {request.org_id}</p>}
      {request.status === 'rejected' && <p className="org-admin-hint">반려 이유: {request.reject_reason || '-'}</p>}
    </article>
  );
};

const OrganizationAdminPage = () => {
  const { currentUser } = useAuth();
  const [status, setStatus] = useState('pending');
  const [requests, setRequests] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [batches, setBatches] = useState([]);
  const [showAllBatches, setShowAllBatches] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    setError('');
    try {
      const [reqRes, orgRes, batchRes] = await Promise.all([
        listOrgAdminRequests(currentUser, status),
        listOrganizations(currentUser),
        listOrgBatches(currentUser),
      ]);
      setRequests(reqRes?.requests || []);
      setOrgs(orgRes?.organizations || []);
      setBatches(batchRes?.batches || []);
    } catch (loadError) {
      setError(loadError.status === 403 ? '운영자 계정으로만 볼 수 있어요.' : loadError.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser, status]);

  useEffect(() => {
    load();
  }, [load]);

  const handleApprove = async (request, candidate) => {
    setBusy(true);
    setNotice('');
    try {
      const result = await approveOrgAdminRequest(currentUser, request.id, {
        source_type: candidate.source_type,
        source_id: candidate.source_id,
        name: request.school_name,
        school_level: request.school_level,
      });
      setNotice(`${request.school_name} 관리자로 승인했어요${result?.org_created ? ' (학교를 새로 만들었어요)' : ''}.`);
      await load();
    } catch (approveError) {
      setError(approveError.message);
    } finally {
      setBusy(false);
    }
  };

  const handleCreate = async (batch) => {
    setBusy(true);
    setNotice('');
    try {
      const result = await createOrganization(currentUser, {
        source_type: batch.source_type,
        source_id: batch.source_id,
        name: batch.name,
      });
      setNotice(`${result?.name || batch.name} 학교를 ${result?.created ? '만들었어요' : '이미 만들어 두었어요'}. 관리자는 신청을 승인하면 붙어요.`);
      await load();
    } catch (createError) {
      setError(createError.message);
    } finally {
      setBusy(false);
    }
  };

  const orphanBatches = batches.filter((b) => !b.org_exists);
  const visibleBatches = showAllBatches ? batches : orphanBatches;

  const handleReject = async (request, reason) => {
    setBusy(true);
    try {
      await rejectOrgAdminRequest(currentUser, request.id, reason);
      setNotice(`${request.school_name} 신청을 반려했어요.`);
      await load();
    } catch (rejectError) {
      setError(rejectError.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="school-page school-page--wide org-admin-page">
      <Helmet>
        <title>학교 관리자 승인 | 관리자</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <header className="school-dash-head">
        <div>
          <p className="school-eyebrow">운영</p>
          <h1>학교 관리자 승인</h1>
        </div>
        <div className="school-tabs" role="tablist" aria-label="신청 상태">
          {STATUS_TABS.map((tab) => (
            <button key={tab.id} type="button" role="tab" aria-selected={status === tab.id} className={status === tab.id ? 'is-active' : ''} onClick={() => setStatus(tab.id)}>
              {tab.label}
            </button>
          ))}
        </div>
      </header>
      <p className="org-admin-rule">구매 이메일이 같아도 자동으로 승인하지 않아요. 구매 내역을 고른 뒤 직접 승인해 주세요.</p>

      {notice && <p className="school-form-notice" role="status">{notice}</p>}
      {error && <p className="school-error" role="alert">{error}</p>}
      {loading && <div className="school-card school-skeleton" />}

      {!loading && !error && (
        <section className="org-admin-list">
          {requests.length === 0 && <p className="school-empty">이 상태의 신청이 없어요.</p>}
          {requests.map((request) => (
            <RequestCard key={request.id} request={request} batches={batches} onApprove={handleApprove} onReject={handleReject} busy={busy} />
          ))}
        </section>
      )}

      {!loading && batches.length > 0 && (
        <section className="school-card org-admin-orgs" data-testid="org-admin-batches">
          <div className="org-admin-batches-head">
            <h2 className="school-section-title">
              10명 이상 단체 구매 {batches.length}건 · 학교 없는 곳 {orphanBatches.length}건
            </h2>
            <button type="button" className="school-btn-ghost" onClick={() => setShowAllBatches((v) => !v)}>
              {showAllBatches ? '학교 없는 곳만' : '전체 보기'}
            </button>
          </div>
          <table>
            <thead>
              <tr><th>학교</th><th>출처</th><th>좌석</th><th>쓴 자리</th><th>남은 자리</th><th>구매일</th><th /></tr>
            </thead>
            <tbody>
              {visibleBatches.map((batch) => (
                <tr key={candidateKey(batch)}>
                  <td>{batch.name || '이름 없음'}</td>
                  <td>{SOURCE_LABEL[batch.source_type]}</td>
                  <td>{batch.quantity}</td>
                  <td>{batch.seats_used}</td>
                  <td>{batch.seats_open}</td>
                  <td>{formatDate(batch.created_at)}</td>
                  <td>
                    {batch.org_exists ? (
                      <span className="org-admin-hint">학교 있음</span>
                    ) : (
                      <button type="button" className="school-btn-secondary" disabled={busy} onClick={() => handleCreate(batch)}>
                        학교 만들기
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {!loading && orgs.length > 0 && (
        <section className="school-card org-admin-orgs">
          <h2 className="school-section-title">학교 {orgs.length}곳</h2>
          <table>
            <thead>
              <tr><th>학교</th><th>플랜</th><th>합류 / 좌석</th><th>빈 자리</th><th>관리자</th><th>출처</th><th>초대 링크</th></tr>
            </thead>
            <tbody>
              {orgs.map((org) => (
                <tr key={org.id}>
                  <td>{org.name}</td>
                  <td>{org.plan_tier === 'enterprise' ? '학교(엔터프라이즈)' : '학교 (검토 없음 · 예전)'}</td>
                  <td>{org.seats_filled} / {org.seat_limit}</td>
                  <td>{org.seats_open}</td>
                  <td>{org.admins}</td>
                  <td>{SOURCE_LABEL[org.source?.type] || '-'}</td>
                  <td><InviteLinkCell token={org.invite_token} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </main>
  );
};

export default OrganizationAdminPage;
